import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mixed, number, object } from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SnippetType, SnippetsSharingAccess } from '@distribute/shared/types';
import { snippetsModel } from '../../../../../../features/snippets';
import { useSnippetsPermissions } from '../../../../../../features/teams';
import { Button } from '../../../../../../shared/ui';
import { RadioButtonForPermissions } from '../../../../../../widgets/editor-sidebar/ui/components/RadioButtonForPermissions';
import { isAllowSwitchSharingAccess } from '../../../../../../features/snippets/lib/helpers';
import { SelectCategoryDropdown } from '../../../../../../features/snippets/ui/components/create-snippet/SelectCategoryDropdown';
import { SettingItemWrapper } from '../../../../../../features/editor-left-sidebar/ui/components/settings/components';
import { ChangePermissionConfirmationModal } from './ChangePermissionConfirmationModal';

export type ConfigureSnippetFormType = {
  sharingAccess: SnippetsSharingAccess;
  categoryId?: number;
};

export const validationSchemaSnippetConfig = object().shape({
  sharingAccess: mixed<SnippetsSharingAccess>().oneOf(
    Object.values(SnippetsSharingAccess)
  ),
  categoryId: number()
    .optional()
    .when('sharingAccess', {
      is: SnippetsSharingAccess.TEAM,
      then: number().required('Please select snippet category.'),
    }),
});

export const AccessSettingsForm: FC = () => {
  const dispatch = useDispatch();
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);

  const categories = useSelector(snippetsModel.selectors.selectCategories);

  const updatingSnippetsData = useSelector(
    snippetsModel.selectors.selectUpdatingSnippetsData
  );

  const currentSnippet = useSelector(
    snippetsModel.selectors.selectCurrentSnippetWithError
  );

  const isUpdateLoading = useSelector(
    snippetsModel.selectors.selectIsUpdateSnippetLoading
  );

  const { isCanCreatePersonalSnippets, isCanManageSnippets } =
    useSnippetsPermissions();

  const defaultSharingAccessValue =
    currentSnippet.sharingAccess ||
    (!isCanCreatePersonalSnippets
      ? SnippetsSharingAccess.TEAM
      : SnippetsSharingAccess.PERSONAL);

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<ConfigureSnippetFormType>({
    resolver: yupResolver(validationSchemaSnippetConfig),
    defaultValues: {
      sharingAccess: defaultSharingAccessValue,
      categoryId: currentSnippet.category?.id,
    },
  });

  const {
    field: { value: categoryIdValue, onChange: setCategoryId },
  } = useController({
    control,
    defaultValue: currentSnippet.category?.id,
    name: 'categoryId',
  });

  const {
    field: { value: sharingAccessValue, onChange: setSharingAccess },
  } = useController({
    control,
    defaultValue: defaultSharingAccessValue,
    name: 'sharingAccess',
  });

  const onChangeSharingAccess = useCallback(
    (value: SnippetsSharingAccess) => {
      setSharingAccess(value);
      if (value === SnippetsSharingAccess.PERSONAL) {
        setCategoryId(undefined);
      }
    },
    [setCategoryId, setSharingAccess]
  );

  useEffect(() => {
    if (
      currentSnippet.sharingAccess === SnippetsSharingAccess.PERSONAL &&
      !isCanCreatePersonalSnippets &&
      isCanManageSnippets
    ) {
      setSharingAccess(SnippetsSharingAccess.TEAM);
    }
  }, [
    dispatch,
    currentSnippet,
    currentSnippet.sharingAccess,
    isCanCreatePersonalSnippets,
    isCanManageSnippets,
    setSharingAccess,
  ]);

  useEffect(() => {
    setSharingAccess(currentSnippet.sharingAccess);
    setCategoryId(currentSnippet.category?.id);
  }, [currentSnippet, setCategoryId, setSharingAccess]);

  const handleFormSubmit = (data: ConfigureSnippetFormType) => {
    if (
      data.sharingAccess === SnippetsSharingAccess.TEAM &&
      currentSnippet.sharingAccess === SnippetsSharingAccess.PERSONAL &&
      !currentSnippet.isDraft
    ) {
      dispatch(snippetsModel.actions.setUpdatingSnippetData(data));

      return;
    }

    dispatch(
      snippetsModel.actions.updateSnippet({
        snippet: currentSnippet,
        snippetId: currentSnippet.id,
        isEditor: true,
        saveAsDraft: Boolean(currentSnippet.isDraft),
        ...data,
        callback: () => {
          return;
        },
      })
    );

    const category = categories.find((c) => c.id === data.categoryId);

    dispatch(
      snippetsModel.actions.setCurrentSnippet({
        ...currentSnippet,
        sharingAccess: data.sharingAccess,
        category:
          data.sharingAccess === SnippetsSharingAccess.PERSONAL
            ? null
            : category,
      })
    );

    setIsEditFormOpen(false);
  };

  const handleToggleForm = () => {
    setIsEditFormOpen((prevIsOpen) => !prevIsOpen);
  };

  useEffect(() => {
    if (isEditFormOpen) {
      reset({
        sharingAccess: defaultSharingAccessValue,
        categoryId: currentSnippet.category?.id,
      });
    }
  }, [
    defaultSharingAccessValue,
    currentSnippet.category?.id,
    currentSnippet,
    isEditFormOpen,
    reset,
  ]);

  return (
    <>
      <ChangePermissionConfirmationModal
        isOpen={Boolean(updatingSnippetsData)}
        onClose={() => {
          setIsEditFormOpen(false);
        }}
        snippet={currentSnippet}
      />
      <SettingItemWrapper
        title="Access settings"
        isHideBorder
        titleButton={
          <Button
            className="h-6 !p-0"
            color={isEditFormOpen ? 'link-destructive' : 'link'}
            variant="text"
            type="button"
            onClick={handleToggleForm}
          >
            {isEditFormOpen ? 'Cancel' : 'Edit'}
          </Button>
        }
      >
        {isEditFormOpen ? (
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <p className="mb-4 text-sm text-gray-500">
              Who can use this snippet? Just you or the entire team?
            </p>

            <div className="flex items-center gap-4 mb-4">
              {Object.values(SnippetsSharingAccess).map((value) => (
                <RadioButtonForPermissions
                  name="sharing-access"
                  key={value}
                  value={value}
                  onChange={() => onChangeSharingAccess(value)}
                  defaultChecked={value === sharingAccessValue}
                  disabled={!isAllowSwitchSharingAccess(value, currentSnippet)}
                />
              ))}
            </div>

            {sharingAccessValue === SnippetsSharingAccess.TEAM && (
              <SelectCategoryDropdown
                setCategoryId={setCategoryId}
                snippetType={SnippetType.TEXT}
                categoryId={categoryIdValue}
                errorText={errors.categoryId?.message}
                isLocalSaving
              />
            )}

            <Button
              color="primary"
              variant="text"
              type="submit"
              className="mt-4"
              loading={isUpdateLoading}
            >
              Save
            </Button>
          </form>
        ) : (
          <span className="text-sm text-gray-600">
            {currentSnippet.sharingAccess === SnippetsSharingAccess.TEAM
              ? 'Team snippet'
              : 'Personal snippet'}
          </span>
        )}
      </SettingItemWrapper>
    </>
  );
};
